import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { storageService } from '../services/storageService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      setIsLoading(true);
      const savedUser = storageService.getUser();
      const token = storageService.getToken();

      if (savedUser && token) {
        // Verificar si el token sigue siendo válido
        const isValid = await authService.validateToken(token);
        if (isValid) {
          setUser(savedUser);
          setIsAuthenticated(true);
        } else {
          // Token inválido, limpiar storage
          storageService.clearAuth();
        }
      }
    } catch (error) {
      console.error('Error al inicializar autenticación:', error);
      storageService.clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await authService.login(email, password);
      
      if (response.success) {
        const { user: userData, token } = response.data;
        setUser(userData);
        setIsAuthenticated(true);
        storageService.saveAuth(userData, token);
        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Error en login:', error);
      return { success: false, message: 'Error de conexión. Intenta nuevamente.' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      storageService.clearAuth();
      // Limpiar sessionStorage
      sessionStorage.removeItem('welcomeShown');
    }
  };

  const updateUser = (userData) => {
    setUser(prevUser => ({ ...prevUser, ...userData }));
    storageService.updateUser(userData);
  };

  const updateUserProfile = async (profileData) => {
    try {
      // Aquí se llamaría al servicio para actualizar el perfil en el backend
      // Por ahora, solo actualizamos localmente
      const updatedData = {
        email: profileData.email,
        phone: profileData.phone,
        avatar: profileData.avatar
      };

      setUser(prevUser => ({ ...prevUser, ...updatedData }));
      storageService.updateUser(updatedData);

      return { success: true };
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      throw error;
    }
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') {
      return user.role === roles;
    }
    return roles.includes(user.role);
  };

  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    updateUserProfile,
    hasRole,
    hasPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};